import { useState, useMemo } from 'react';
import { X, Calculator, Plus, Trash2 } from 'lucide-react';
import { calcRowTotal, fmt, fmtInput, mkId } from './ProductCosting';

/** 제작비 항목 선택지 (내용은 값이 다양해 직접 입력) */
const MFG_ITEMS = ['원자재', '부자재'];

function newMfgRow() {
  return { id: mkId(), 항목: '원자재', 내용: '', 단가: '', 단위: '식', 수량: '1', 비고: '' };
}

/** 장비별 단가 기준값. 전력단가는 원/kWh, 구매가는 만원. */
const EQUIPMENT = {
  H2D: { power: 184, price만원: 300 },
  P1S: { power: 184, price만원: 150 },
  A1:  { power: 184, price만원: 50 },
};
const EQUIP_KEYS = Object.keys(EQUIPMENT);

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

/** 운영비 기본 2행 — 장비운용비 / 감가상각비 (고정 행) */
function defaultOpRows() {
  return [
    { id: mkId(), 항목: '장비운용비', 장비: 'H2D', 수량: '1', 비고: '1kWh * 184원' },
    { id: mkId(), 항목: '감가상각비', 장비: 'H2D', 수량: '1', 만원: '300', 시간: '7000' },
  ];
}

/** 장비운용비 단가 = 장비 전력단가 */
function opUnit(row) {
  return EQUIPMENT[row.장비]?.power ?? 0;
}
/** 감가상각비 단가 = 구매가(만원 → 원) / 수명시간 */
function depUnit(row) {
  const h = num(row.시간);
  if (h <= 0) return 0;
  return (num(row.만원) * 10000) / h;
}
function opRowUnit(row) {
  return row.항목 === '감가상각비' ? depUnit(row) : opUnit(row);
}
function opRowTotal(row) {
  return opRowUnit(row) * num(row.수량);
}

/**
 * 견적서 품목별 원가계산 모달.
 * 저장은 Yjs가 아니라 견적 품목(item.costing)에 담는다 — items가 JSON 컬럼이라 서버 변경 불필요.
 */
export default function EstimateCostingModal({ item, onClose, onApply }) {
  const saved = item?.costing || {};

  // 구버전 저장분의 항목명(본품/서포트 등)은 현재 선택지로 맞춰준다
  const [mfgRows, setMfgRows] = useState(() =>
    (saved.mfgRows || []).map(r => ({
      ...r,
      id: r.id || mkId(),
      항목: MFG_ITEMS.includes(r.항목) ? r.항목 : MFG_ITEMS[0],
    }))
  );
  // 이 원가계산이 몇 개로 이루어진 세트인지 → 품목의 수량1에 연동되고 단가 산출의 분모가 된다
  const [setQty, setSetQty] = useState(() => saved.setQty ?? '1');
  // 구버전(자유형식 운영비)으로 저장된 데이터는 새 구조로 초기화한다
  const [opRows, setOpRows] = useState(() => {
    const rows = saved.opRows;
    const ok = Array.isArray(rows) && rows.length === 2 && rows.every(r => r.장비);
    return ok ? rows : defaultOpRows();
  });
  const [labor, setLabor] = useState(() => saved.labor || { 만원: '2', 시간: '0.25' });
  const [profitPct, setProfitPct] = useState(() => saved.profitPct ?? '150');
  // 직접 입력한 금액(자동계산 해제). 빈 문자열이면 자동계산.
  const [summary, setSummary] = useState(() => saved.summary || {
    재료비: '', 운영비: '', 인건비: '', 합계: '', 이윤: '', 총합: '',
  });

  const auto = useMemo(() => {
    const 재료비 = mfgRows.reduce((s, r) => s + calcRowTotal(r), 0);
    const 운영비 = opRows.reduce((s, r) => s + opRowTotal(r), 0);
    // 인건비 = 시급(만원) × 작업시간
    const 인건비 = num(labor.만원) * 10000 * num(labor.시간);
    const 합계 = 재료비 + 운영비 + 인건비;
    const 이윤 = 합계 * (num(profitPct) / 100);
    const 총합 = 합계 + 이윤;
    return { 재료비, 운영비, 인건비, 합계, 이윤, 총합 };
  }, [mfgRows, opRows, labor, profitPct]);

  const valueOf = (key) => {
    const ov = summary[key];
    if (ov !== '' && ov !== undefined && ov !== null) {
      const n = parseFloat(String(ov).replace(/,/g, ''));
      return isNaN(n) ? auto[key] : n;
    }
    return auto[key];
  };
  const display = (key) => {
    const ov = summary[key];
    if (ov !== '' && ov !== undefined && ov !== null) return fmtInput(ov);
    return fmt(auto[key]);
  };

  // 단가 = 총합 ÷ 세트 구성수량 (구성수량이 0이면 나누지 않음)
  const setQtyNum = num(setQty);
  const applied = Math.round(setQtyNum > 0 ? valueOf('총합') / setQtyNum : valueOf('총합'));

  const editOp = (id, key, val) => setOpRows(rows => rows.map(r => {
    if (r.id !== id) return r;
    const next = { ...r, [key]: val };
    // 장비를 바꾸면 감가상각비의 구매가도 해당 장비 기준으로 갱신
    if (key === '장비' && r.항목 === '감가상각비') next.만원 = String(EQUIPMENT[val]?.price만원 ?? '');
    if (key === '장비' && r.항목 === '장비운용비') next.비고 = `1kWh * ${EQUIPMENT[val]?.power ?? 0}원`;
    return next;
  }));

  const editMfg = (id, key, val) =>
    setMfgRows(rows => rows.map(r => (r.id === id ? { ...r, [key]: val } : r)));

  const sectionCls = 'bg-gray-900 border border-gray-800 rounded-2xl p-4';
  const selectCls = 'bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-sm text-white focus:outline-none focus:border-brand w-full';
  const sumInputCls = 'bg-transparent text-sm focus:outline-none text-right w-36 border-b border-transparent focus:border-gray-600';
  const cellInput = 'w-full bg-transparent text-sm text-white focus:outline-none placeholder-gray-700';
  const miniInput = 'bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-sm text-white w-16 text-right focus:outline-none focus:border-brand';
  const tdCls = 'px-2 py-1.5';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="bg-gray-950 border border-gray-700 rounded-2xl w-full max-w-4xl h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Calculator size={16} className="text-brand flex-shrink-0" />
            <h3 className="font-bold text-gray-100 truncate">원가계산</h3>
            {item?.title && <span className="text-xs text-gray-500 truncate">· {item.title}</span>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 flex flex-col gap-4">
          {/* 1. 제작비 */}
          <div className={sectionCls}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">1. 제작비</p>
              <p className="text-sm text-gray-400">
                합계 <span className="text-white font-medium">{fmt(auto.재료비)}원</span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-24`}>항목</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-28`}>내용</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-24`}>단가</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-12`}>단위</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-20`}>수량</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium`}>비고</th>
                    <th className={`${tdCls} text-right text-gray-500 font-medium w-24`}>합계</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {mfgRows.length === 0 && (
                    <tr><td colSpan={8} className="py-6 text-center text-gray-600 text-sm">+ 행 추가로 제작 비용을 입력하세요</td></tr>
                  )}
                  {mfgRows.map(row => (
                    <tr key={row.id} className="border-b border-gray-800/60 hover:bg-gray-800/30 group">
                      <td className={tdCls}>
                        <select value={row.항목} onChange={e => editMfg(row.id, '항목', e.target.value)} className={selectCls}>
                          {MFG_ITEMS.map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </td>
                      <td className={tdCls}>
                        <input className={cellInput} value={row.내용 || ''} placeholder="예: PLA"
                          onChange={e => editMfg(row.id, '내용', e.target.value)} />
                      </td>
                      <td className={tdCls}>
                        <input className={cellInput} value={fmtInput(row.단가)} placeholder="—"
                          onChange={e => editMfg(row.id, '단가', e.target.value.replace(/,/g, ''))} />
                      </td>
                      <td className={tdCls}>
                        <input className={cellInput} value={row.단위 || ''} placeholder="식"
                          onChange={e => editMfg(row.id, '단위', e.target.value)} />
                      </td>
                      <td className={tdCls}>
                        <input className={cellInput} value={fmtInput(row.수량)} placeholder="—"
                          onChange={e => editMfg(row.id, '수량', e.target.value.replace(/,/g, ''))} />
                      </td>
                      <td className={tdCls}>
                        <input className={cellInput} value={row.비고 || ''} placeholder="—"
                          onChange={e => editMfg(row.id, '비고', e.target.value)} />
                      </td>
                      <td className={`${tdCls} text-right text-gray-300 whitespace-nowrap`}>
                        {calcRowTotal(row) > 0 ? fmt(calcRowTotal(row)) : '—'}
                      </td>
                      <td className="pr-1">
                        <button onClick={() => setMfgRows(rows => rows.filter(r => r.id !== row.id))}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-600 hover:text-red-400 transition-all">
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={() => setMfgRows(rows => [...rows, newMfgRow()])}
                className="mt-2 flex items-center gap-1 text-sm text-gray-600 hover:text-brand px-2 py-1 transition-colors">
                <Plus size={11} /> 행 추가
              </button>
            </div>
          </div>

          {/* 2. 운영비 — 장비 선택 기반 고정 2행 */}
          <div className={sectionCls}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">2. 운영비</p>
              <p className="text-sm text-gray-400">
                합계 <span className="text-white font-medium">{fmt(auto.운영비)}원</span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-24`}>항목</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-24`}>내용</th>
                    <th className={`${tdCls} text-right text-gray-500 font-medium w-20`}>단가</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-12`}>단위</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium w-20`}>수량</th>
                    <th className={`${tdCls} text-left text-gray-500 font-medium`}>비고</th>
                    <th className={`${tdCls} text-right text-gray-500 font-medium w-24`}>합계</th>
                  </tr>
                </thead>
                <tbody>
                  {opRows.map(row => (
                    <tr key={row.id} className="border-b border-gray-800/60">
                      <td className={`${tdCls} text-gray-300 whitespace-nowrap`}>{row.항목}</td>
                      <td className={tdCls}>
                        <select
                          value={row.장비}
                          onChange={e => editOp(row.id, '장비', e.target.value)}
                          className="bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-sm text-white focus:outline-none focus:border-brand"
                        >
                          {EQUIP_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </td>
                      <td className={`${tdCls} text-right text-gray-300 whitespace-nowrap`}>
                        {fmt(opRowUnit(row))}
                      </td>
                      <td className={`${tdCls} text-gray-500`}>시간</td>
                      <td className={tdCls}>
                        <input className={cellInput} value={fmtInput(row.수량)}
                          onChange={e => editOp(row.id, '수량', e.target.value.replace(/,/g, ''))} />
                      </td>
                      <td className={tdCls}>
                        {row.항목 === '감가상각비' ? (
                          <div className="flex items-center gap-1 text-sm text-gray-400 whitespace-nowrap">
                            (<input className={miniInput} value={fmtInput(row.만원)}
                              onChange={e => editOp(row.id, '만원', e.target.value.replace(/,/g, ''))} />)만원
                            <span className="mx-0.5">/</span>
                            (<input className={miniInput} value={fmtInput(row.시간)}
                              onChange={e => editOp(row.id, '시간', e.target.value.replace(/,/g, ''))} />)시간
                          </div>
                        ) : (
                          <input className={cellInput} value={row.비고 || ''}
                            onChange={e => editOp(row.id, '비고', e.target.value)} />
                        )}
                      </td>
                      <td className={`${tdCls} text-right text-gray-300 whitespace-nowrap`}>
                        {opRowTotal(row) > 0 ? fmt(opRowTotal(row)) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. 합계 */}
          <div className={sectionCls}>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-3">3. 합계</p>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="py-1.5 text-left text-sm text-gray-500 font-medium w-40">항목</th>
                  <th className="py-1.5 text-left text-sm text-gray-500 font-medium">산출내역</th>
                  <th className="py-1.5 text-right text-sm text-gray-500 font-medium w-40">금액 (원)</th>
                </tr>
              </thead>
              <tbody>
                {['재료비', '운영비'].map(key => (
                  <tr key={key} className="border-b border-gray-800/60">
                    <td className="py-2 text-sm text-gray-400">{key}</td>
                    <td />
                    <td className="py-2 text-right">
                      <input className={`${sumInputCls} text-gray-300`}
                        value={display(key)} placeholder={fmt(auto[key])}
                        onChange={e => setSummary(s => ({ ...s, [key]: e.target.value.replace(/,/g, '') }))} />
                    </td>
                  </tr>
                ))}

                {/* 인건비 — 산출내역 직접 입력 */}
                <tr className="border-b border-gray-800/60">
                  <td className="py-2 text-sm text-gray-400">인건비</td>
                  <td className="py-2">
                    <div className="flex items-center gap-1 text-sm text-gray-400 whitespace-nowrap">
                      (<input className={miniInput} value={fmtInput(labor.만원)}
                        onChange={e => setLabor(l => ({ ...l, 만원: e.target.value.replace(/,/g, '') }))} />)만원
                      <span className="mx-0.5">×</span>
                      (<input className={miniInput} value={fmtInput(labor.시간)}
                        onChange={e => setLabor(l => ({ ...l, 시간: e.target.value.replace(/,/g, '') }))} />)시간
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    <input className={`${sumInputCls} text-gray-300`}
                      value={display('인건비')} placeholder={fmt(auto.인건비)}
                      onChange={e => setSummary(s => ({ ...s, 인건비: e.target.value.replace(/,/g, '') }))} />
                  </td>
                </tr>

                <tr className="border-b border-gray-800/60">
                  <td className="py-2 text-sm text-white font-semibold">합계</td>
                  <td />
                  <td className="py-2 text-right">
                    <input className={`${sumInputCls} text-white font-semibold`}
                      value={display('합계')} placeholder={fmt(auto.합계)}
                      onChange={e => setSummary(s => ({ ...s, 합계: e.target.value.replace(/,/g, '') }))} />
                  </td>
                </tr>

                {/* 이윤 — 비율 직접 입력 */}
                <tr className="border-b border-gray-800/60">
                  <td className="py-2 text-sm text-gray-400">이윤</td>
                  <td className="py-2">
                    <div className="flex items-center gap-1 text-sm text-gray-400 whitespace-nowrap">
                      합계의 (<input className={miniInput} value={fmtInput(profitPct)}
                        onChange={e => setProfitPct(e.target.value.replace(/,/g, ''))} />)%
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    <input className={`${sumInputCls} text-gray-300`}
                      value={display('이윤')} placeholder={fmt(auto.이윤)}
                      onChange={e => setSummary(s => ({ ...s, 이윤: e.target.value.replace(/,/g, '') }))} />
                  </td>
                </tr>

                <tr className="border-b border-gray-800/60">
                  <td className="py-2 text-sm text-white font-semibold">총합</td>
                  <td />
                  <td className="py-2 text-right">
                    <input className={`${sumInputCls} text-white font-semibold`}
                      value={display('총합')} placeholder={fmt(auto.총합)}
                      onChange={e => setSummary(s => ({ ...s, 총합: e.target.value.replace(/,/g, '') }))} />
                  </td>
                </tr>

                {/* 세트 구성수량 — 품목의 수량1로 연동되고 단가의 분모가 된다 */}
                <tr className="border-b border-gray-800/60">
                  <td className="py-2 text-sm text-gray-400">세트 구성수량</td>
                  <td className="py-2">
                    <div className="flex items-center gap-1 text-sm text-gray-400 whitespace-nowrap">
                      이 원가는 (<input className={miniInput} value={fmtInput(setQty)}
                        onChange={e => setSetQty(e.target.value.replace(/,/g, ''))} />)개 기준
                    </div>
                  </td>
                  <td className="py-2 text-right text-sm text-gray-500">{fmtInput(setQty) || 0}개</td>
                </tr>

                <tr className="bg-brand/10">
                  <td className="py-2 text-sm text-white font-semibold">단가 → 적용</td>
                  <td className="py-2 text-xs text-gray-500">총합 ÷ 세트 구성수량</td>
                  <td className="py-2 text-right text-white font-semibold pr-[9px]">{fmt(applied)}</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-2 text-[10px] text-gray-600">
              금액을 직접 수정하면 자동계산이 해제됩니다. 빈칸으로 두면 자동 계산됩니다.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-800 flex-shrink-0">
          <span className="text-xs text-gray-500">
            단가 <span className="text-brand font-bold text-sm mx-1">{fmt(applied)}원</span>
            · 수량 <span className="text-gray-300 font-semibold">{fmtInput(setQty) || 0}개</span>로 적용
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200">취소</button>
            <button
              onClick={() => onApply(applied, setQtyNum || 1, { mfgRows, opRows, labor, profitPct, summary, setQty })}
              className="px-5 py-2 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand/80 transition-colors"
            >
              단가로 적용
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
